import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { DashboardWidget, DashboardWidgetFC } from '@frontend/grid-dashboard';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { customROITimePeriods } from '../constants';
import { CustomTimeHeader } from './custom-time-header';

/**
 * @dashboard-widget
 *
 * id: no-show-rate
 * title: No-Show Rate
 * description: Track no-show performance for your location with historical trends
 * icon: user
 */

export const NoShowRateWidget: DashboardWidgetFC = () => {
  const timePeriod = customROITimePeriods.lastMonth;
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const filters = {
    startDate: dayjs(timePeriod.value.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    endDate: dayjs(timePeriod.value.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    locationId: selectedLocationIds,
  };

  const { data: nsrAggregatesData, isLoading: aggregateDataLoading } =
    AnalyticsGatewayV1.Queries.useGetAppointmentsNsrAggregatesQuery(filters, {
      enabled: selectedLocationIds.length > 0,
    });

  return (
    <DashboardWidget data-trackingid={trackingIds.roi.noShowRateWidget}>
      <DashboardWidget.Header>
        <CustomTimeHeader label={timePeriod.label} />
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.widgetContent}>
        <Text
          weight='bold'
          css={{ fontSize: theme.fontSize(40), lineHeight: 1, marginTop: 'var(--fabric-space-padding-sm)' }}
        >
          {formatters.appendPercent(isDemoAccount ? 12 : nsrAggregatesData?.noShowRate ?? 0)}
        </Text>
        <ContentLoader show={aggregateDataLoading} size='small' />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

NoShowRateWidget.config = {
  size: 'small-narrow',
  feature: 'analytics',
};

const styles = {
  widgetContent: css`
    position: relative;
    overflow: hidden;
  `,

  chartContainer: css`
    height: ${theme.spacing(5)};
    width: 100%;
  `,
};
